// ─── Canal d'application d'une session de prise en main ───
// Un ticket à usage unique (30 s) est demandé en REST, puis le WebSocket est
// ouvert aussitôt : le JWT ne passe jamais dans l'URL. Le relais met en attente
// les messages envoyés avant que l'agent ne rejoigne le canal.

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.jsx';
import { raisonFermeture, urlWebSocket } from './protocol.js';

/**
 * @param {string} sessionId
 * @param {string} application  ecran | terminal | fichiers | clavardage
 * @param {(e: MessageEvent) => void} onMessage
 * @param {(send: (m: object) => void) => void} [onOpen]
 * @param {{enabled?: boolean, restartKey?: any, onClose?: (code: number) => void}} [opts]
 *        restartKey : un changement rouvre le canal (ex. compte du terminal).
 */
export function useChannel(sessionId, application, onMessage, onOpen, opts = {}) {
  const { enabled = true, restartKey = 0, onClose } = opts;
  const [etat, setEtat] = useState('connexion');
  const [raison, setRaison] = useState('');
  const ws = useRef(null);
  const msgRef = useRef(onMessage);
  const openRef = useRef(onOpen);
  const closeRef = useRef(onClose);
  msgRef.current = onMessage;
  openRef.current = onOpen;
  closeRef.current = onClose;

  const envoyer = useCallback((m) => {
    if (ws.current?.readyState === WebSocket.OPEN) ws.current.send(JSON.stringify(m));
  }, []);

  // Trames binaires (téléversement) et remplissage du tampon d'envoi, pour la contre-pression.
  const envoyerBinaire = useCallback((buf) => {
    if (ws.current?.readyState === WebSocket.OPEN) ws.current.send(buf);
  }, []);
  const enAttente = useCallback(() => ws.current?.bufferedAmount || 0, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let annule = false;
    let socket = null;
    setEtat('connexion');
    setRaison('');
    (async () => {
      let path;
      try {
        const r = await api.fetch('/platform/remote/sessions/' + encodeURIComponent(sessionId) + '/channels', {
          method: 'POST',
          body: JSON.stringify({ application }),
        });
        if (!r.ok) {
          let msg = 'Ouverture du canal impossible (' + r.status + ').';
          try {
            const j = await r.json();
            if (j?.error) msg = j.error;
          } catch { /* corps non JSON */ }
          if (!annule) { setEtat('ferme'); setRaison(msg); }
          return;
        }
        path = (await r.json()).path;
      } catch (e) {
        if (!annule) { setEtat('ferme'); setRaison('Ouverture du canal impossible : ' + (e.message || e)); }
        return;
      }
      if (annule) return;
      socket = new WebSocket(urlWebSocket(path));
      socket.binaryType = 'arraybuffer';
      ws.current = socket;
      socket.onopen = () => {
        setEtat('ouvert');
        openRef.current?.(envoyer);
      };
      socket.onmessage = (e) => msgRef.current?.(e);
      socket.onclose = (e) => {
        if (ws.current !== socket) return;
        setEtat('ferme');
        setRaison(raisonFermeture(e.code, e.reason));
        closeRef.current?.(e.code);
      };
    })();
    return () => {
      annule = true;
      if (socket) {
        if (ws.current === socket) ws.current = null;
        socket.close();
      }
    };
  }, [sessionId, application, enabled, restartKey, envoyer]);

  return { etat, raison, envoyer, envoyerBinaire, enAttente };
}
